/**
 * ForgotPasswordForm Component
 *
 * Form for requesting password reset with email.
 * Follows the same design patterns as LoginForm for consistency.
 *
 * @module components/auth/ForgotPasswordForm
 */

import React, { useState, useCallback } from 'react';
import { cn } from '@/utils/cn';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { FormField } from '@/components/ui/FormField';
import { useTheme } from '@/hooks/useTheme';
import { getAuthService } from '@/services/auth/AuthServiceFactory';
import type { ForgotPasswordFormData } from '@/types/auth';
import { getLogger } from '@/services/shared/LoggerFactory';

/**
 * ForgotPasswordForm component props interface
 */
interface ForgotPasswordFormProps {
  /** Success callback after successful password reset request */
  onSuccess?: () => void;
  /** Callback to switch back to login mode */
  onBackToLogin?: () => void;
  /** Custom class name */
  className?: string;
}

/**
 * ForgotPasswordForm component for password reset requests
 *
 * @example
 * ```tsx
 * <ForgotPasswordForm
 *   onSuccess={handleResetSuccess}
 *   onBackToLogin={handleBackToLogin}
 * />
 * ```
 */
export function ForgotPasswordForm({ onSuccess, onBackToLogin, className = '' }: ForgotPasswordFormProps) {
  const logger = getLogger();
  const { isDark } = useTheme();
  const authService = getAuthService();

  // Form state
  const [formData, setFormData] = useState<ForgotPasswordFormData>({
    email: '',
  });

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Field-level errors
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});

  /**
   * Handle field value changes
   */
  const handleFieldChange = useCallback((field: keyof ForgotPasswordFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Clear global error
    if (error) {
      setError(null);
    }
  }, [fieldErrors, error]);

  /**
   * Validate all form fields
   */
  const validateForm = useCallback((): boolean => {
    const errors: Record<string, string> = {};

    // Email validation
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!authService.validateEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    setFieldErrors(errors);

    // Mark all fields as touched to show errors
    setTouchedFields({
      email: true
    });

    return Object.keys(errors).length === 0;
  }, [formData]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (isLoading) return;

    // Validate form
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await authService.sendPasswordResetEmail(formData.email);

      if (response.status === 'OK') {
        setIsSubmitted(true);
        onSuccess?.();
      } else if (response.status === 'EMAIL_DOES_NOT_EXIST_ERROR') {
        setError('No account found with this email address. Please check your email or sign up for a new account.');
      }
    } catch (error) {
      logger.error('Password reset error:', error);
      setError('Failed to send password reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, validateForm, formData, onSuccess]);

  /**
   * Get field validation props
   */
  const getFieldValidation = useCallback((fieldName: keyof ForgotPasswordFormData) => {
    const shouldShowError = touchedFields[fieldName] && fieldErrors[fieldName];

    return {
      error: shouldShowError ? fieldErrors[fieldName] : undefined
    };
  }, [touchedFields, fieldErrors]);

  // Show success message after submission
  if (isSubmitted) {
    return (
      <div className={cn('w-full', className)}>
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className={cn(
            "w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center",
            isDark ? "bg-green-900/20" : "bg-green-50"
          )}>
            <svg className={cn(
              "w-8 h-8",
              isDark ? "text-green-400" : "text-green-600"
            )} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className={cn(
            "text-2xl font-bold mb-2",
            isDark ? "text-secondary-100" : "text-secondary-900"
          )}>
            Check Your Email
          </h1>
          <p className={cn(
            isDark ? "text-secondary-400" : "text-secondary-600"
          )}>
            We've sent a password reset link to <strong>{formData.email}</strong>
          </p>
        </div>

        {/* Instructions */}
        <div className={cn(
          "p-4 rounded-lg mb-6",
          isDark ? "bg-secondary-800" : "bg-secondary-50"
        )}>
          <p className={cn(
            "text-sm",
            isDark ? "text-secondary-300" : "text-secondary-700"
          )}>
            Please check your email and click the link to reset your password.
            If you don't see the email, check your spam folder.
          </p>
        </div>

        {/* Back to Login */}
        {onBackToLogin && (
          <div className="text-center">
            <button
              type="button"
              onClick={onBackToLogin}
              className={cn(
                "font-medium hover:underline",
                isDark ? "text-primary-400" : "text-primary-600"
              )}
            >
              ← Back to Login
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={cn('w-full', className)}>
      {/* Form Header */}
      <div className="text-left mb-8">
        <h1 className={cn(
          "text-2xl font-bold mb-2",
          isDark ? "text-secondary-100" : "text-secondary-900"
        )}>
          Reset Your Password
        </h1>
        <p className={cn(
          isDark ? "text-secondary-400" : "text-secondary-600"
        )}>
          Enter your email address and we'll send you a link to reset your password
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6" noValidate data-testid="forgot-password-form">
        {/* Email Field */}
        <FormField
          id="email"
          label="Email Address"
          required
          {...getFieldValidation('email')}
        >
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => handleFieldChange('email', e.target.value)}
            placeholder="Enter your email address"
            autoComplete="email"
            disabled={isLoading}
            required
            fullWidth
            data-testid="email-input"
          />
        </FormField>

        {/* Global Error */}
        {error && (
          <div className={cn(
            "p-4 border rounded-lg",
            isDark ? "bg-red-900/20 border-red-800" : "bg-red-50 border-red-200"
          )}>
            <div className="flex items-start">
              <svg className={cn(
                "w-5 h-5 mt-0.5 mr-3 flex-shrink-0",
                isDark ? "text-red-400" : "text-red-600"
              )} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 className={cn(
                  "text-sm font-medium",
                  isDark ? "text-red-200" : "text-red-800"
                )}>
                  Reset Failed
                </h3>
                <p className={cn(
                  "text-sm mt-1",
                  isDark ? "text-red-300" : "text-red-700"
                )}>
                  {error}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          variant="primary"
          size="md"
          loading={isLoading}
          disabled={isLoading}
          className="w-full"
          data-testid="reset-password-button"
        >
          {isLoading ? 'Sending Reset Link...' : 'Send Reset Link'}
        </Button>

        {/* Back to Login */}
        {onBackToLogin && (
          <div className="text-center">
            <p className={cn(
              "text-sm",
              isDark ? "text-secondary-400" : "text-secondary-600"
            )}>
              Remember your password?{' '}
              <button
                type="button"
                onClick={onBackToLogin}
                disabled={isLoading}
                className={cn(
                  "font-medium hover:underline",
                  isDark ? "text-primary-400" : "text-primary-600",
                  isLoading && "opacity-50 cursor-not-allowed"
                )}
              >
                Back to Login
              </button>
            </p>
          </div>
        )}
      </form>
    </div>
  );
}